/**
 * Vending — Customer Interest Form — H3
 * Changes:
 *  - Removed "Business Address (combined)" field
 *  - Merged "Business Information" + "Vending Profile" into one section:
 *      "Business Information / Vending Profile"
 *  - Vertical view submit button is now left-aligned under the form
 */

import React, { useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Col, Form, ProgressBar, Row } from "react-bootstrap";
import { VENDING_INTEREST_COPY_V3, IVendingInterestCopyUnit } from "./Vending-CustomerInterest-v03-strings";

/* ============================== CONFIG ============================== */
const VERSION = "vending-customer-interest-v03";
const SHOW_VERSION = false;

const TEST_MODE = false;
const API_GATEWAY_URL = "https://wi8zkeven2.execute-api.us-east-1.amazonaws.com/prod/feedback";

const DEMO_MODE_DEFAULT = false;
const DEMO_MODE_UI_ENABLED = false;

type ViewMode = "wizard" | "vertical";
type FormShape = Record<string, unknown>;
type SetForm = (patch: FormShape) => void;

export interface IWizardSection {
  id: string;
  title: React.ReactNode;
  explanation: React.ReactNode;
  enabled?: boolean;
  render: (form: FormShape, setForm: SetForm) => React.ReactNode;
}

const clampPct = (p: number) => Math.min(99, Math.max(0, Math.round(p)));

/* ============================== DATA ============================== */
const US_STATES: { value: string; label: string }[] = [
  { value: "AL", label: "Alabama" }, { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" }, { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" }, { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" }, { value: "DE", label: "Delaware" },
  { value: "DC", label: "District of Columbia" }, { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" }, { value: "HI", label: "Hawaii" },
  { value: "ID", label: "Idaho" }, { value: "IL", label: "Illinois" },
  { value: "IN", label: "Indiana" }, { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" }, { value: "KY", label: "Kentucky" },
  { value: "LA", label: "Louisiana" }, { value: "ME", label: "Maine" },
  { value: "MD", label: "Maryland" }, { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" }, { value: "MN", label: "Minnesota" },
  { value: "MS", label: "Mississippi" }, { value: "MO", label: "Missouri" },
  { value: "MT", label: "Montana" }, { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" }, { value: "NH", label: "New Hampshire" },
  { value: "NJ", label: "New Jersey" }, { value: "NM", label: "New Mexico" },
  { value: "NY", label: "New York" }, { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" }, { value: "OH", label: "Ohio" },
  { value: "OK", label: "Oklahoma" }, { value: "OR", label: "Oregon" },
  { value: "PA", label: "Pennsylvania" }, { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" }, { value: "SD", label: "South Dakota" },
  { value: "TN", label: "Tennessee" }, { value: "TX", label: "Texas" },
  { value: "UT", label: "Utah" }, { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" }, { value: "WA", label: "Washington" },
  { value: "WV", label: "West Virginia" }, { value: "WI", label: "Wisconsin" },
  { value: "WY", label: "Wyoming" },
];

/* ============================== HELPERS ============================== */
const S = (v: unknown): string | null => {
  if (v == null) return null;
  if (typeof v === "string") {
    const t = v.trim();
    return t === "" ? null : t;
  }
  return String(v);
};
const hasVal = (v: unknown): boolean => (typeof v === "string" ? v.trim().length > 0 : v != null);

/* ============================== COMPLETION SCHEMA ============================== */
type SectionKeys = {
  id: string;
  required: (form: FormShape) => string[];
  optional?: (form: FormShape) => string[];
};

const completionSchema: SectionKeys[] = [
  {
    id: "businessProfile",
    required: () => [
      "businessName",
      "address1",
      "city",
      "state",
      "zip",
      "employeesOnsite",
      "dailyVisitors",
    ], // address2 optional
  },
  { id: "contact", required: () => ["contactName", "role", "email", "phone"] },
  { id: "notes", required: () => [], optional: () => ["notes"] },
];

const requiredKeysForSection = (sectionId: string, form: FormShape): string[] =>
  completionSchema.find((s) => s.id === sectionId)?.required(form) ?? [];

const optionalKeysForSection = (sectionId: string, form: FormShape): string[] =>
  completionSchema.find((s) => s.id === sectionId)?.optional?.(form) ?? [];

const sectionIsComplete = (sectionId: string, form: FormShape): boolean => {
  const req = requiredKeysForSection(sectionId, form);
  if (req.length > 0) return req.every((k) => hasVal(form[k]));
  const opt = optionalKeysForSection(sectionId, form);
  return opt.length > 0 && opt.some((k) => hasVal(form[k]));
};

const overallCompletion = (enabledSectionIds: string[], form: FormShape): number => {
  const allReq: string[] = enabledSectionIds.flatMap((id) => requiredKeysForSection(id, form));
  const uniq = Array.from(new Set(allReq));
  if (uniq.length === 0) return 0;
  const filled = uniq.reduce((acc, k) => (hasVal(form[k]) ? acc + 1 : acc), 0);
  return (filled / uniq.length) * 100;
};

/* ============================== CONTROLS ============================== */
const WizardControls: React.FC<{
  onPrev: () => void;
  onNext: () => void;
  onSubmit: () => void;
  isFirst: boolean;
  isLast: boolean;
  submitting?: boolean;
  copy: IVendingInterestCopyUnit;
}> = ({ onPrev, onNext, onSubmit, isFirst, isLast, submitting, copy }) => (
  <div className="d-flex justify-content-between">
    <Button variant="secondary" onClick={onPrev} disabled={isFirst || !!submitting}>
      {copy.buttons.previous}
    </Button>
    {isLast ? (
      <Button variant="success" onClick={onSubmit} disabled={!!submitting}>
        {submitting ? "Processing..." : copy.buttons.submit}
      </Button>
    ) : (
      <Button variant="primary" onClick={onNext} disabled={!!submitting}>
        {copy.buttons.next}
      </Button>
    )}
  </div>
);

/* ============================== WIZARD CORE ============================== */
export const DualViewWizard_VendingInterest_H3: React.FC<{
  mode?: ViewMode;
  sections: IWizardSection[];
  initialForm?: FormShape;
  className?: string;
  onSubmit?: (form: FormShape) => void;
  submitting?: boolean;
  submitted?: boolean;
  copy: IVendingInterestCopyUnit;
}> = ({
  mode = "wizard",
  sections,
  initialForm = {},
  className,
  onSubmit,
  submitting = false,
  submitted = false,
  copy,
}) => {
  const enabledSections = useMemo(
    () => sections.filter((s) => s.enabled !== false),
    [sections]
  );

  const [form, setFormState] = useState<FormShape>(initialForm);
  const setForm: SetForm = (patch) => setFormState((prev) => ({ ...prev, ...patch }));

  const [current, setCurrent] = useState(0);
  const [activeId, setActiveId] = useState<string>(enabledSections[0]?.id ?? "");

  const ids = useMemo(() => enabledSections.map((s) => s.id), [enabledSections]);
  const activeIndex = Math.max(0, ids.indexOf(activeId));
  const total = Math.max(1, enabledSections.length);
  const enabledIds = enabledSections.map((s) => s.id);

  const rawPct = overallCompletion(enabledIds, form);
  const pctLabel = submitted ? 100 : clampPct(rawPct);
  const pct = submitted ? 100 : clampPct(rawPct);

  const sectionShowsSuccess = (sectionId: string): boolean => sectionIsComplete(sectionId, form);

  if (mode === "vertical") {
    return (
      <div className={className}>
        {/* <Card className="position-sticky mb-3" style={{ top: 16, zIndex: 1 }}>
          <Card.Header>
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <strong>
                  {copy.appTitle} {SHOW_VERSION ? `— ${VERSION}` : ""} · Section {activeIndex + 1} of {total}
                </strong>{" "}
                — {enabledSections[activeIndex]?.title}
              </div>
              <div style={{ minWidth: 180 }}>
                <ProgressBar now={pct} label={`${pctLabel}%`} />
              </div>
            </div>
          </Card.Header>
        </Card> */}

        {enabledSections.map((s) => {
          const isActive = activeId === s.id;
          const successVisual = sectionShowsSuccess(s.id);



// bg-secondary-subtle  // Lighter Gray
// bg-success-subtle    // Darker gray 
// FORM BACKGROUND 
          const cardClasses = [
            "border",
            successVisual ? "border-success bg-success-subtle" : "bg-secondary-subtle",
            isActive && !successVisual ? "border-primary" : "",
          ].join(" ").trim();

          const headerClasses = isActive ? "bg-primary text-white" : "";

          return (
            <Row key={s.id} className="mb-3" style={{ scrollMarginTop: 90 }}>
              <Col
                md={8}
                onClick={() => setActiveId(s.id)}
                onFocusCapture={() => setActiveId(s.id)}
              >
                <Card className={cardClasses}>
                  <Card.Header className={headerClasses}>
                    {s.title}
                    {successVisual && !isActive && <Badge bg="success" className="ms-2">{copy.buttons.completeBadge}</Badge>}
                    {successVisual && isActive && <Badge bg="light" text="dark" className="ms-2">{copy.buttons.completeBadge}</Badge>}
                  </Card.Header>
                  <Card.Body>{s.render(form, setForm)}</Card.Body>
                </Card>
              </Col>

              <Col md={4}>
                <Card className={isActive ? "border border-primary" : "border"}>
                  <Card.Header className={isActive ? "bg-primary text-white" : ""}>
                    {copy.sectionHelpHeader}
                  </Card.Header>
                  {/* HELP TEXT BOX */}
                  <Card.Body className="bg-secondary-subtle">
                    <strong>{s.title}</strong>
                    <p className="mb-0">{s.explanation}</p>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          );
        })}

        {/* Submit button LEFT under the form (vertical view) */}
        
        <Col md={8}>
        <div className="d-flex justify-content-end mt-3">
          <Button variant="success" onClick={() => onSubmit?.(form)} disabled={!!submitting}>
            {submitting ? "Processing..." : copy.buttons.submit}
          </Button>
        </div>
        </Col>
      
      </div>


    );
  }

  const step = enabledSections[current];
  const successVisualForStep = sectionShowsSuccess(step.id);
  const cardClasses = successVisualForStep ? "border border-success bg-success-subtle" : "";

  return (
    <Card className={`${className ?? ""} ${cardClasses}`}>
      <Card.Header>
        <div className="d-flex align-items-center justify-content-between">
          <div>
            <strong>
              {copy.appTitle} {SHOW_VERSION ? `— ${VERSION}` : ""} · Step {current + 1} of {total}
            </strong>{" "}
            — {step.title}
          </div>
          <div style={{ minWidth: 160 }}>
            <ProgressBar now={pct} label={`${pctLabel}%`} />
          </div>
        </div>
      </Card.Header>

      <Card.Body>
        {step.render(form, setForm)}

        <Card className="mt-3">
          <Card.Header>{copy.sectionHelpHeader}</Card.Header>
          <Card.Body>
            <strong>{step.title}</strong>
            <p className="mb-0">{step.explanation}</p>
          </Card.Body>
        </Card>

        <hr />
        <WizardControls
          onPrev={() => setCurrent((c) => Math.max(0, c - 1))}
          onNext={() => setCurrent((c) => Math.min(total - 1, c + 1))}
          onSubmit={() => onSubmit?.(form)}
          isFirst={current === 0}
          isLast={current === total - 1}
          submitting={submitting}
          copy={copy}
        />
      </Card.Body>
    </Card>
  );
};

/* ============================== APP SHELL (EXAMPLE USAGE) ============================== */
export const VendingInterest_v03: React.FC = () => {
  const currentCopy: IVendingInterestCopyUnit = VENDING_INTEREST_COPY_V3.en[
    Math.min(0, VENDING_INTEREST_COPY_V3.en.length - 1)
  ];

  const [uiMode, setUiMode] = useState<ViewMode>("vertical");
  const [demoMode, setDemoMode] = useState<boolean>(DEMO_MODE_DEFAULT);

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const initialBase: FormShape = {};

  const DEMO_DATA: FormShape = {
    // Business + Profile (merged)
    businessName: "Ridgeway Logistics LLC",
    address1: "123 Ridgeway Dr",
    address2: "Suite 200",
    city: "Denver",
    state: "CO",
    zip: "80202",
    employeesOnsite: "120",
    dailyVisitors: "60",
    // Contact
    contactName: "Jane Smith",
    role: "Facilities Manager",
    email: "jane.smith@example.com",
    phone: "(720) 555-0155",
    // Notes
    notes: "Prefer installs after 4pm; dock access available.",
  };

  const sections: IWizardSection[] = useMemo(() => {
    const copy = currentCopy;

    const businessProfile: IWizardSection = {
      id: "businessProfile",
      title: copy.businessProfile.title,
      explanation: copy.businessProfile.help,
      enabled: true,
      render: (form, setForm) => (
        <Form>
          <Row className="g-3">
            {/* Business Name */}
            <Col md={6}>
              <Form.Group controlId="businessName">
                <Form.Label>{copy.businessProfile.fields.businessName.label}</Form.Label>
                <Form.Control
                  type="text"
                  value={(form.businessName as string) ?? ""}
                  onChange={(e) => setForm({ businessName: e.target.value })}
                  placeholder={copy.businessProfile.fields.businessName.placeholder}
                  isValid={hasVal(form.businessName)}
                />
              </Form.Group>
            </Col>


           <Col md={6}>
              <Form.Group controlId="">
                <Form.Label></Form.Label>
                {/* <Form.Control
                  type="text"
                  value={(form.businessName as string) ?? ""}
                  onChange={(e) => setForm({ businessName: e.target.value })}
                  placeholder={copy.businessProfile.fields.businessName.placeholder}
                  isValid={hasVal(form.businessName)}
                /> */}
              </Form.Group>
            </Col>

            {/* Address 1 / 2 */}
            <Col md={6}>
              <Form.Group controlId="address1">
                <Form.Label>{copy.businessProfile.fields.address1.label}</Form.Label>
                <Form.Control
                  type="text"
                  value={(form.address1 as string) ?? ""}
                  onChange={(e) => setForm({ address1: e.target.value })}
                  placeholder={copy.businessProfile.fields.address1.placeholder}
                  autoComplete="address-line1"
                  isValid={hasVal(form.address1)}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group controlId="address2">
                <Form.Label>{copy.businessProfile.fields.address2.label}</Form.Label>
                <Form.Control
                  type="text"
                  value={(form.address2 as string) ?? ""}
                  onChange={(e) => setForm({ address2: e.target.value })}
                  placeholder={copy.businessProfile.fields.address2.placeholder}
                  autoComplete="address-line2"
                  isValid={hasVal(form.address2)}
                />
              </Form.Group>
            </Col>

            {/* City / State / Zip */}
            <Col md={4}>
              <Form.Group controlId="city">
                <Form.Label>{copy.businessProfile.fields.city.label}</Form.Label>
                <Form.Control
                  type="text"
                  value={(form.city as string) ?? ""}
                  onChange={(e) => setForm({ city: e.target.value })}
                  placeholder={copy.businessProfile.fields.city.placeholder}
                  isValid={hasVal(form.city)}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group controlId="state">
                <Form.Label>{copy.businessProfile.fields.state.label}</Form.Label>
                <Form.Select
                  value={(form.state as string) ?? ""}
                  onChange={(e) => setForm({ state: e.target.value })}
                  isValid={hasVal(form.state)}
                >
                  <option value="">{copy.businessProfile.fields.state.placeholder}</option>
                  {US_STATES.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group controlId="zip">
                <Form.Label>{copy.businessProfile.fields.zip.label}</Form.Label>
                <Form.Control
                  type="text"
                  value={(form.zip as string) ?? ""}
                  onChange={(e) => setForm({ zip: e.target.value })}
                  placeholder={copy.businessProfile.fields.zip.placeholder}
                  inputMode="numeric"
                  isValid={hasVal(form.zip)}
                />
              </Form.Group>
            </Col>

            {/* Profile: Employees / Visitors */}
            <Col md={6}>
              <Form.Group controlId="employeesOnsite">
                <Form.Label>{copy.businessProfile.fields.employeesOnsite.label}</Form.Label>
                <Form.Control
                  type="text"
                  value={(form.employeesOnsite as string) ?? ""}
                  onChange={(e) => setForm({ employeesOnsite: e.target.value })}
                  placeholder={copy.businessProfile.fields.employeesOnsite.placeholder}
                  inputMode="numeric"
                  isValid={hasVal(form.employeesOnsite)}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="dailyVisitors">
                <Form.Label>{copy.businessProfile.fields.dailyVisitors.label}</Form.Label>
                <Form.Control
                  type="text"
                  value={(form.dailyVisitors as string) ?? ""}
                  onChange={(e) => setForm({ dailyVisitors: e.target.value })}
                  placeholder={copy.businessProfile.fields.dailyVisitors.placeholder}
                  inputMode="numeric"
                  isValid={hasVal(form.dailyVisitors)}
                />
              </Form.Group>
            </Col>
          </Row>
        </Form>
      ),
    };

    const contact: IWizardSection = {
      id: "contact",
      title: copy.contact.title,
      explanation: copy.contact.help,
      enabled: true,
      render: (form, setForm) => (
        <Form>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group controlId="contactName">
                <Form.Label>{copy.contact.fields.contactName.label}</Form.Label>
                <Form.Control
                  type="text"
                  value={(form.contactName as string) ?? ""}
                  onChange={(e) => setForm({ contactName: e.target.value })}
                  placeholder={copy.contact.fields.contactName.placeholder}
                  isValid={hasVal(form.contactName)}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="role">
                <Form.Label>{copy.contact.fields.role.label}</Form.Label>
                <Form.Control
                  type="text"
                  value={(form.role as string) ?? ""}
                  onChange={(e) => setForm({ role: e.target.value })}
                  placeholder={copy.contact.fields.role.placeholder}
                  isValid={hasVal(form.role)}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group controlId="email">
                <Form.Label>{copy.contact.fields.email.label}</Form.Label>
                <Form.Control
                  type="email"
                  value={(form.email as string) ?? ""}
                  onChange={(e) => setForm({ email: e.target.value })}
                  placeholder={copy.contact.fields.email.placeholder}
                  isValid={hasVal(form.email)}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="phone">
                <Form.Label>{copy.contact.fields.phone.label}</Form.Label>
                <Form.Control
                  type="text"
                  value={(form.phone as string) ?? ""}
                  onChange={(e) => setForm({ phone: e.target.value })}
                  placeholder={copy.contact.fields.phone.placeholder}
                  inputMode="tel"
                  isValid={hasVal(form.phone)}
                />
              </Form.Group>
            </Col>
          </Row>
        </Form>
      ),
    };

    const notes: IWizardSection = {
      id: "notes",
      title: copy.notes.title,
      explanation: copy.notes.help,
      enabled: true,
      render: (form, setForm) => (
        <Form>
          <Form.Group controlId="notes">
            <Form.Label>{copy.notes.fields.notes.label}</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={(form.notes as string) ?? ""}
              onChange={(e) => setForm({ notes: e.target.value })}
              placeholder={copy.notes.fields.notes.placeholder}
              isValid={hasVal(form.notes)}
            />
          </Form.Group>


      
          
        </Form>
      ),
    };

    return [businessProfile, contact, notes];
  }, [currentCopy]);

  const initialForm = demoMode
    ? {
        // Business + Profile (merged)
        businessName: "Ridgeway Logistics LLC",
        address1: "123 Ridgeway Dr",
        address2: "Suite 200",
        city: "Denver",
        state: "CO",
        zip: "80202",
        employeesOnsite: "120",
        dailyVisitors: "60",
        // Contact
        contactName: "Jane Smith",
        role: "Facilities Manager",
        email: "jane.smith@example.com",
        phone: "(720) 555-0155",
        // Notes
        notes: "Prefer installs after 4pm; dock access available.",
      }
    : initialBase;

  // const [submitting, setSubmitting] = useState(false);
  // const [submitted, setSubmitted] = useState(false);
  // const [submitError, setSubmitError] = useState<string | null>(null);

  const handleSubmit = async (payload: FormShape) => {
    setSubmitError(null);
    setSubmitting(true);

    const enabledIds = sections.filter(s => s.enabled !== false).map(s => s.id);
    const raw = overallCompletion(enabledIds, payload);
    const pctToSend = clampPct(raw);

    const customer_DATA = {
      productMode: "vending_interest",
      submittedAtIso: new Date().toISOString(),
      source: `Vending Interest — ${VERSION}`,

      // Business + Profile (merged)
      businessName: S(payload.businessName),
      address1: S(payload.address1),
      address2: S(payload.address2),
      city: S(payload.city),
      state: S(payload.state),
      zip: S(payload.zip),
      employeesOnsite: S(payload.employeesOnsite),
      dailyVisitors: S(payload.dailyVisitors),

      // Contact
      contactName: S(payload.contactName),
      role: S(payload.role),
      email: S(payload.email),
      phone: S(payload.phone),

      // Notes
      notes: S(payload.notes),

      // Progress metric
      formCompletionPercent: String(pctToSend),

      // Meta
      domainName: String(window.location.hostname),
      itemCategory: String("Customer.vending.interest"),
      itemClass: String("lead.request"),
      itemType: String("legacy.vending.interest"),
      clientVersion: String(VERSION + ".lead"),
    };

    if (TEST_MODE) {
      console.log("TEST MODE — customer_DATA:", customer_DATA);
      setSubmitting(false);
      return;
    }

    try {
      const resp = await fetch(API_GATEWAY_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(customer_DATA),
      });
      if (!resp.ok) throw new Error("network");
      setSubmitted(true);
    } catch {
      setSubmitError("Error: network error, try again in a minute.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="container my-5">
        <Row className="justify-content-center">
          <Col md={8}>
            <Card>
              <Card.Header className="fw-bold">{currentCopy.submitThankYou.header}</Card.Header>
              <Card.Body>
                <p className="mb-0">{currentCopy.submitThankYou.body}</p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    );
  }

  return (
    <div className="container my-4 bg-secondary text-white pt-3">
      {/* Page Header + Mode Switches + Demo */}
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
        <h3 className="m-0">
         {currentCopy.appTitle} {SHOW_VERSION ? `— ${VERSION}` : ""}
        </h3>
        <div className="d-flex align-items-center gap-3">
          {DEMO_MODE_UI_ENABLED && (
            <Form.Check
              type="checkbox"
              id="demoModeToggle"
              label={currentCopy.buttons.demoToggle}
              checked={demoMode}
              onChange={(e) => setDemoMode(e.target.checked)}
              disabled={submitting}
            />
          )}
          <div className="d-flex gap-2">
            {/* <Button
              variant={uiMode === "wizard" ? "primary" : "outline-primary"}
              onClick={() => setUiMode("wizard")}
              disabled={submitting}
            >
              {currentCopy.buttons.wizardView}
            </Button>
            <Button
              variant={uiMode === "vertical" ? "primary" : "outline-primary"}
              onClick={() => setUiMode("vertical")}
              disabled={submitting}
            >
              {currentCopy.buttons.verticalView}
            </Button> */}
          </div>
        </div>
      </div>

      {submitError && <Alert variant="danger" className="mb-3">{submitError}</Alert>}

      <DualViewWizard_VendingInterest_H3
        mode={uiMode}
        sections={sections}
        initialForm={initialForm}
        onSubmit={handleSubmit}
        submitting={submitting}
        submitted={false}
        copy={currentCopy}
      />
    </div>
  );
};

export default VendingInterest_v03;
